import { useState } from "react";
import { ApiError, api } from "@/lib/apiClient";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Field";
import { Alert } from "@/components/ui/Alert";

interface EmployeeImpact {
  hero_user_id: number;
  display_name?: string | null;
  email?: string | null;
  project_count: number;
}

interface PreviewResponse {
  dry_run?: boolean;
  preview?: {
    projects_with_local_assignments_cleared: number;
    projects_with_hero_manual_assignments: number;
    local_assignments_by_employee: EmployeeImpact[];
    hero_manual_assignments_by_employee: EmployeeImpact[];
  };
  cleared_projects?: number;
  released_hero_projects?: number;
  [key: string]: unknown;
}

export function StripHeroSection() {
  const [dryRun, setDryRun] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PreviewResponse | null>(null);

  async function trigger() {
    if (!dryRun) {
      const ok = window.confirm(
        "Alle lokalen Hero-Zuweisungen bei abgeschlossenen Projekten löschen und die zugehörigen Mitarbeiter in Hero vom Projekt abmelden?",
      );
      if (!ok) return;
    }

    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const path = `/admin/strip-hero-assignments-completed-projects?dry_run=${dryRun ? "true" : "false"}`;
      const data = await api.post<PreviewResponse>(path);
      setResult(data);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Anfrage fehlgeschlagen.",
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <Card
      title="Abgeschlossene Projekte – Hero-Zuweisungen"
      description="Entfernt für fertiggestellte Projekte die lokalen Hero-Benutzer-IDs und hebt manuelle Mitarbeiter-Zuweisungen in Hero auf."
    >
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={trigger} loading={running} variant="warning">
          Zuweisungen bereinigen
        </Button>
        <Checkbox
          id="strip-dry-run"
          label="Nur Vorschau"
          checked={dryRun}
          onChange={(e) => setDryRun(e.target.checked)}
        />
      </div>

      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
        Mit Vorschau werden betroffene Projektanzahlen und Mitarbeiter aus DB und Hero geladen (kann bei
        vielen Projekten einige Sekunden dauern).
      </p>

      <div className="mt-3">
        {error && (
          <Alert variant="error" title="Fehler">
            {error}
          </Alert>
        )}
        {!error && result && <ResultPanel result={result} />}
      </div>
    </Card>
  );
}

function ResultPanel({ result }: { result: PreviewResponse }) {
  const preview = result.preview;
  if (preview) {
    return (
      <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Counter
            label="Projekte (lokal zu bereinigen)"
            value={preview.projects_with_local_assignments_cleared}
          />
          <Counter
            label="Projekte (Hero manuelle Zuweisungen)"
            value={preview.projects_with_hero_manual_assignments}
          />
        </div>
        <EmployeeList title="Lokale Zuweisungen je Mitarbeiter" rows={preview.local_assignments_by_employee} />
        <EmployeeList
          title="Hero manuelle Zuweisungen je Mitarbeiter"
          rows={preview.hero_manual_assignments_by_employee}
        />
      </div>
    );
  }

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200">
      <pre className="whitespace-pre-wrap break-words text-xs">{JSON.stringify(result, null, 2)}</pre>
    </div>
  );
}

function Counter({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
      <div className="text-xl font-semibold">{value ?? 0}</div>
    </div>
  );
}

function EmployeeList({ title, rows }: { title: string; rows: EmployeeImpact[] }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div>
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {title}
      </div>
      <ul className="space-y-1 text-xs">
        {rows.map((r) => (
          <li key={r.hero_user_id}>
            <span className="font-medium">{r.display_name || `User #${r.hero_user_id}`}</span>{" "}
            <span className="text-slate-400">({r.email || "—"})</span> · {r.project_count} Projekte
          </li>
        ))}
      </ul>
    </div>
  );
}
